import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Pagination,
    Popover,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { manageOffsiteCampaigns } from '../../redux/buzzbridge/actions';
import {
    managedCampaignsSelector,
    gettingManagedCampaignsSelector,
    activeBrandDashboardBrandSelector,
} from '../../redux/buzzbridge/selectors';
import type { RootState } from '../../redux/root-reducer';
import type { ICampaign } from '../../redux/buzzbridge/types';

import { BrandInfluencerFiltersPopover } from './BrandInfluencerFiltersPopover';
import {
    countActiveInfluencerFilters,
    DEFAULT_INFLUENCER_SEARCH_FILTERS,
    fetchInfluencerSearch,
    type InfluencerSearchFilters,
    type PlatformFilter,
} from './brandInfluencerSearch';
import { MetaTags } from '../../components/Metatags/Metatags';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER_HIGHEST,
} from './brandDashboardTokens';
import type { IInfluencer } from '../../redux/buzzbridge/types';
import { InstagramGlyph, TiktokGlyph, YoutubeGlyph } from '../../components/BuzzBridge/SocialPlatformSvgs';
import { formatFollowers, formatEngagement } from './CreatorProfileModal';
import { CreatorPublicProfileDrawer } from '../../components/PublicInfluencerProfile/CreatorPublicProfileDrawer';
import { InfluencerInviteToCampaignModal } from '../../components/PublicInfluencerProfile/InfluencerInviteToCampaignModal';
import {
    creatorPrimaryCategoryLabel,
    getInfluencerAccountUserName,
} from '../../components/PublicInfluencerProfile/publicInfluencerProfileUtils';
import { BrandTalentTab } from './BrandTalentTab';
import type { ITalentProfile } from './BrandTalentTab';
import { MessageDialogue } from '../../components/MessageDialogue/MessageDialogue';
import { CreatorMessageRequiresCampaignModal } from '../../components/PublicInfluencerProfile/CreatorMessageRequiresCampaignModal';
import { canMessageCreator } from '../../utils/creatorMessageGate';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { savedInfluencersUrl } from '../../configurations/api/url';

const PAGE_LIMIT = 18;

type FindTalentTab = 'influencer' | 'talent';

function parseFindTalentTab(param: string | null): FindTalentTab {
    return param === 'talent' ? 'talent' : 'influencer';
}

function getPlatformFollowers(influencer: IInfluencer, platform: PlatformFilter): number | undefined {
    if (platform === 'all') {
        if (influencer.totalReach) return influencer.totalReach;
        const sum =
            (influencer.instagram?.followers ?? 0) +
            (influencer.tiktok?.followers ?? 0) +
            (influencer.youtube?.followers ?? 0) +
            (influencer.facebook?.followers ?? 0) +
            (influencer.twitter?.followers ?? 0);
        return sum > 0 ? sum : undefined;
    }
    return influencer[platform]?.followers;
}

export default function BrandFindTalentPage() {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeBrand = useSelector(activeBrandDashboardBrandSelector);
    const campaigns: ICampaign[] = useSelector(managedCampaignsSelector);
    const loadingCampaigns = useSelector(gettingManagedCampaignsSelector);
    const token: string = useSelector((s: RootState) => s.authentication.token);
    const [favoritesOnly, setFavoritesOnly] = useState(false);

    // ─── Tab (synced with ?tab=influencer|talent) ─────────────────────────────
    const activeTab = parseFindTalentTab(searchParams.get('tab'));

    const setActiveTab = useCallback(
        (tab: FindTalentTab) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    next.set('tab', tab);
                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    // ─── Shared invite modal ──────────────────────────────────────────────────
    const [inviteTarget, setInviteTarget] = useState<IInfluencer | ITalentProfile | null>(null);
    const [profileTarget, setProfileTarget] = useState<IInfluencer | null>(null);
    const [messageTarget, setMessageTarget] = useState<IInfluencer | null>(null);
    const [campaignGateOpen, setCampaignGateOpen] = useState(false);
    const [campaignGateCreatorName, setCampaignGateCreatorName] = useState<string | undefined>();

    // ─── Influencer state ─────────────────────────────────────────────────────
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
    const [draftFilters, setDraftFilters] = useState<InfluencerSearchFilters>(DEFAULT_INFLUENCER_SEARCH_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState<InfluencerSearchFilters>(DEFAULT_INFLUENCER_SEARCH_FILTERS);

    const activeFilterCount = countActiveInfluencerFilters(appliedFilters);

    const [creators, setCreators] = useState<IInfluencer[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const abortRef = useRef<AbortController | null>(null);

    // ─── Influencer fetch ─────────────────────────────────────────────────────
    const fetchInfluencers = useCallback(
        async (opts: {
            search: string;
            page: number;
            filters: InfluencerSearchFilters;
            favoritesOnly: boolean;
            brandId?: string;
            campaignId?: string;
        }) => {
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setLoading(true);
            try {
                const result = await fetchInfluencerSearch(
                    opts.filters,
                    opts.search,
                    opts.page,
                    PAGE_LIMIT,
                    controller.signal,
                    token || undefined,
                    opts.favoritesOnly,
                    {
                        brandId: opts.brandId,
                        campaignId: opts.campaignId,
                        sortBy: opts.brandId ? 'brand_match' : undefined,
                        sortOrder: opts.brandId ? 'desc' : undefined,
                    },
                );
                if (!controller.signal.aborted) {
                    setCreators(result.influencers);
                    setTotalPages(result.totalPages);
                    setTotalCount(result.totalCount);
                }
            } catch (err: any) {
                if (!axios.isCancel(err)) {
                    setCreators([]);
                    setTotalPages(1);
                    setTotalCount(0);
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const campaignIdFromUrl = searchParams.get('campaign_id') || searchParams.get('campaign') || undefined;

    useEffect(() => {
        if (activeTab !== 'influencer') return;
        fetchInfluencers({
            search,
            page,
            filters: appliedFilters,
            favoritesOnly,
            brandId: activeBrand?._id,
            campaignId: campaignIdFromUrl || undefined,
        });
    }, [search, page, appliedFilters, activeTab, favoritesOnly, fetchInfluencers, activeBrand?._id, campaignIdFromUrl]);

    useEffect(() => () => abortRef.current?.abort(), []);

    // ─── Favourite toggle ─────────────────────────────────────────────────────
    const toggleFavorite = useCallback(
        async (creator: IInfluencer) => {
            if (!token) return;
            const isFav = creator.is_favorited;
            // Optimistic update
            setCreators(prev => prev.map(c => (c._id === creator._id ? { ...c, is_favorited: !isFav } : c)));
            try {
                if (isFav) {
                    await axios.delete(`${savedInfluencersUrl}/${creator._id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                } else {
                    await axios.post(
                        savedInfluencersUrl,
                        { influencer_id: creator._id },
                        { headers: { Authorization: `Bearer ${token}` } },
                    );
                }
            } catch {
                // Revert on error
                setCreators(prev => prev.map(c => (c._id === creator._id ? { ...c, is_favorited: isFav } : c)));
            }
        },
        [token],
    );

    // ─── Campaign load for invite ─────────────────────────────────────────────
    useEffect(() => {
        if (inviteTarget) {
            dispatch(
                manageOffsiteCampaigns({
                    ...(activeBrand?._id && { brand: activeBrand._id }),
                    sort: '-createdAt',
                }),
            );
        }
    }, [inviteTarget, dispatch, activeBrand?._id]);

    // ─── Influencer filter handlers ───────────────────────────────────────────
    const handleOpenFilters = (el: HTMLElement) => {
        setDraftFilters({ ...appliedFilters });
        setFilterAnchor(el);
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...draftFilters });
        setPage(1);
        setFilterAnchor(null);
    };

    const handleClearFilters = () => {
        setDraftFilters(DEFAULT_INFLUENCER_SEARCH_FILTERS);
        setAppliedFilters(DEFAULT_INFLUENCER_SEARCH_FILTERS);
        setPage(1);
        setFilterAnchor(null);
    };

    return (
        <MetaTags title="Find talent | Brand | Thspian" description="Discover creators and talent for your campaigns">
            <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 1280, mx: 'auto', width: '100%', pb: 4 }}>
                {/* ── Hero — always visible ── */}
                <Box sx={{ mb: 4 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
                            fontWeight: 800,
                            letterSpacing: '-0.03em',
                            color: '#ffffff',
                            lineHeight: 1.15,
                            mb: 1,
                        }}>
                        Find your{' '}
                        <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY }}>
                            Signature
                        </Box>{' '}
                        Voice.
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: { xs: 16, md: 18 },
                            color: '#adaaaa',
                            maxWidth: 520,
                            lineHeight: 1.5,
                        }}>
                        Search the elite network of creators curated for high-impact brand storytelling.
                    </Typography>
                </Box>

                {/* ════ INFLUENCER TAB ════ */}
                {activeTab === 'influencer' && (
                    <>
                        <Box sx={{ mb: 4 }}>
                            {/* ── Tab switcher — right above search ── */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    mb: 2,
                                    p: 0.5,
                                    bgcolor: BRAND_SURFACE_CONTAINER,
                                    borderRadius: 999,
                                    width: 'fit-content',
                                    gap: 0.5,
                                }}>
                                {[
                                    { key: 'influencer' as const, label: 'Influencers' },
                                    { key: 'talent' as const, label: 'Talent' },
                                ].map(tab => (
                                    <Button
                                        key={tab.key}
                                        onClick={() => setActiveTab(tab.key)}
                                        disableRipple={activeTab === tab.key}
                                        sx={{
                                            borderRadius: 999,
                                            textTransform: 'none',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 13,
                                            px: 3,
                                            py: 0.75,
                                            minWidth: 110,
                                            bgcolor: activeTab === tab.key ? BRAND_LUXE_PRIMARY : 'transparent',
                                            color: activeTab === tab.key ? '#392700' : '#adaaaa',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                bgcolor:
                                                    activeTab === tab.key
                                                        ? BRAND_LUXE_PRIMARY
                                                        : 'rgba(255,255,255,0.06)',
                                                color: activeTab === tab.key ? '#392700' : '#fff',
                                            },
                                        }}>
                                        {tab.label}
                                    </Button>
                                ))}
                            </Box>

                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search by name, niche, or keyword..."
                                    value={searchInput}
                                    onChange={e => setSearchInput(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ color: '#adaaaa', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            bgcolor: '#262626',
                                            borderRadius: 999,
                                            py: 0.75,
                                            pl: 1,
                                            pr: 2,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            fontSize: 15,
                                            color: '#ffffff',
                                            '& fieldset': { border: 'none' },
                                            '&:hover fieldset': { border: 'none' },
                                            '&.Mui-focused fieldset': { border: 'none' },
                                            '&.Mui-focused': { boxShadow: '0 0 0 1px rgba(255,201,101,0.35)' },
                                        },
                                        '& .MuiInputBase-input::placeholder': {
                                            color: 'rgba(173,170,170,0.5)',
                                            opacity: 1,
                                        },
                                    }}
                                />
                                <IconButton
                                    onClick={() => {
                                        setFavoritesOnly(f => !f);
                                        setPage(1);
                                    }}
                                    sx={{
                                        flexShrink: 0,
                                        width: 48,
                                        height: 48,
                                        borderRadius: '50%',
                                        bgcolor: favoritesOnly ? 'rgba(255,105,180,0.12)' : '#262626',
                                        color: favoritesOnly ? '#ff69b4' : '#adaaaa',
                                        border: favoritesOnly
                                            ? '1px solid rgba(255,105,180,0.35)'
                                            : '1px solid transparent',
                                        '&:hover': { bgcolor: 'rgba(255,105,180,0.15)', color: '#ff69b4' },
                                    }}>
                                    {favoritesOnly ? (
                                        <FavoriteIcon sx={{ fontSize: 20 }} />
                                    ) : (
                                        <FavoriteBorderIcon sx={{ fontSize: 20 }} />
                                    )}
                                </IconButton>
                                <Badge
                                    badgeContent={activeFilterCount}
                                    color="warning"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            bgcolor: BRAND_LUXE_PRIMARY,
                                            color: '#392700',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 10,
                                        },
                                    }}>
                                    <IconButton
                                        onClick={e => handleOpenFilters(e.currentTarget)}
                                        sx={{
                                            flexShrink: 0,
                                            width: 48,
                                            height: 48,
                                            borderRadius: '50%',
                                            bgcolor: activeFilterCount > 0 ? 'rgba(255,201,101,0.12)' : '#262626',
                                            color: activeFilterCount > 0 ? BRAND_LUXE_PRIMARY : '#adaaaa',
                                            border:
                                                activeFilterCount > 0
                                                    ? `1px solid rgba(255,201,101,0.3)`
                                                    : '1px solid transparent',
                                            '&:hover': { bgcolor: 'rgba(255,201,101,0.15)', color: BRAND_LUXE_PRIMARY },
                                        }}>
                                        <TuneIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                </Badge>
                            </Box>

                            <Popover
                                open={Boolean(filterAnchor)}
                                anchorEl={filterAnchor}
                                onClose={() => setFilterAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{
                                    sx: {
                                        mt: 1,
                                        bgcolor: '#1a1919',
                                        border: '1px solid rgba(255,255,255,0.07)',
                                        borderRadius: 3,
                                        p: 2.5,
                                        width: 340,
                                        maxHeight: 'min(85vh, 720px)',
                                        overflowY: 'auto',
                                    },
                                }}>
                                <BrandInfluencerFiltersPopover
                                    draft={draftFilters}
                                    onDraftChange={patch => setDraftFilters(prev => ({ ...prev, ...patch }))}
                                    onApply={handleApplyFilters}
                                    onClear={handleClearFilters}
                                />
                            </Popover>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 11,
                                    bgcolor: loading ? 'rgba(255,255,255,0.05)' : 'rgba(0,253,193,0.1)',
                                    color: loading ? '#adaaaa' : BRAND_LUXE_SECONDARY,
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 1,
                                    letterSpacing: '0.06em',
                                }}>
                                {loading ? '…' : `${totalCount} FOUND`}
                            </Typography>
                        </Box>

                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
                                <CircularProgress size={40} sx={{ color: BRAND_LUXE_PRIMARY }} />
                            </Box>
                        ) : creators.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 2,
                                    py: 12,
                                    border: '2px dashed rgba(255,255,255,0.06)',
                                    borderRadius: 3,
                                }}>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 2,
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                    <PersonOutlineIcon sx={{ fontSize: 30, color: 'rgba(255,255,255,0.15)' }} />
                                </Box>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 15,
                                        color: 'rgba(255,255,255,0.4)',
                                    }}>
                                    No creators found
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        fontSize: 13,
                                        color: 'rgba(255,255,255,0.25)',
                                        textAlign: 'center',
                                        maxWidth: 300,
                                    }}>
                                    {search
                                        ? `No creators matched "${search}". Try a different keyword.`
                                        : 'No creators are available for this filter yet.'}
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                                    gap: { xs: 3, md: 4 },
                                }}>
                                {creators.map(creator => (
                                    <CreatorCard
                                        key={creator._id}
                                        creator={creator}
                                        platform={appliedFilters.platform}
                                        simple
                                        onInvite={() => setInviteTarget(creator)}
                                        onViewProfile={() => setProfileTarget(creator)}
                                        onMessage={() => {
                                            if (!canMessageCreator(creator.can_message)) {
                                                setCampaignGateCreatorName(creator.name);
                                                setCampaignGateOpen(true);
                                                return;
                                            }
                                            setMessageTarget(creator);
                                        }}
                                        onFavorite={() => toggleFavorite(creator)}
                                    />
                                ))}
                            </Box>
                        )}

                        {!loading && totalPages > 1 && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 5 }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(_, p) => setPage(p)}
                                    size="medium"
                                    sx={{
                                        '& .MuiPaginationItem-root': { color: '#adaaaa', fontSize: 13 },
                                        '& .MuiPaginationItem-root.Mui-selected': {
                                            bgcolor: BRAND_LUXE_PRIMARY,
                                            color: '#392700',
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}

                {/* ════ TALENT TAB ════ */}
                {activeTab === 'talent' && <BrandTalentTab activeTab={activeTab} onTabChange={setActiveTab} />}
            </Box>

            <CreatorPublicProfileDrawer
                open={Boolean(profileTarget)}
                onClose={() => setProfileTarget(null)}
                userName={profileTarget ? getInfluencerAccountUserName(profileTarget) : undefined}
                initialInfluencer={profileTarget}
                onInvite={() => {
                    setInviteTarget(profileTarget);
                    setProfileTarget(null);
                }}
            />

            {/* Invite to campaign modal — shared by both tabs */}
            <InfluencerInviteToCampaignModal
                open={Boolean(inviteTarget)}
                target={inviteTarget}
                campaigns={campaigns}
                loadingCampaigns={loadingCampaigns}
                token={token}
                onClose={() => setInviteTarget(null)}
            />

            {/* DM dialogue */}
            {messageTarget && (
                <MessageDialogue
                    open={Boolean(messageTarget)}
                    onClose={() => setMessageTarget(null)}
                    recipient={
                        typeof messageTarget.created_by === 'object' && messageTarget.created_by !== null
                            ? ({
                                  _id: messageTarget.created_by._id ?? '',
                                  firstName: messageTarget.created_by.firstName ?? messageTarget.name ?? '',
                                  lastName: messageTarget.created_by.lastName ?? '',
                                  profile_picture:
                                      messageTarget.created_by.profile_picture ?? messageTarget.profile_picture ?? '',
                                  verified: messageTarget.created_by.verified ?? false,
                                  role: '',
                                  audme_profile: '',
                              } as any)
                            : undefined
                    }
                />
            )}

            <CreatorMessageRequiresCampaignModal
                open={campaignGateOpen}
                onClose={() => setCampaignGateOpen(false)}
                creatorName={campaignGateCreatorName}
            />
        </MetaTags>
    );
}

// ─── Creator card (influencer tab only) ───────────────────────────────────────
function CreatorCard({
    creator,
    platform,
    simple = false,
    onInvite,
    onViewProfile,
    onMessage,
    onFavorite,
}: {
    creator: IInfluencer;
    platform: PlatformFilter;
    /** When true: followers in the engagement slot; hide followers beside the name. */
    simple?: boolean;
    onInvite: () => void;
    onViewProfile: () => void;
    onMessage: () => void;
    onFavorite: () => void;
}) {
    const {
        name,
        profile_picture,
        categories,
        country,
        avg_engagement_rate,
        influencer_score,
        instagram,
        tiktok,
        youtube,
    } = creator;

    const category = creatorPrimaryCategoryLabel(categories);

    const followers = getPlatformFollowers(creator, platform);

    const activePlatforms: { key: string; icon: React.ReactNode; color: string }[] = [
        ...(instagram?.followers
            ? [
                  {
                      key: 'instagram',
                      icon: <InstagramGlyph sx={{ width: 13, height: 13 }} color="#E4405F" />,
                      color: '#E4405F',
                  },
              ]
            : []),
        ...(tiktok?.followers
            ? [
                  {
                      key: 'tiktok',
                      icon: <TiktokGlyph sx={{ width: 13, height: 13 }} color="#FE2C55" />,
                      color: '#FE2C55',
                  },
              ]
            : []),
        ...(youtube?.followers
            ? [
                  {
                      key: 'youtube',
                      icon: <YoutubeGlyph sx={{ width: 13, height: 13 }} color="#FF0000" />,
                      color: '#FF0000',
                  },
              ]
            : []),
    ];

    return (
        <Box
            sx={{
                bgcolor: BRAND_SURFACE_CONTAINER,
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.05)',
                transition: 'box-shadow 0.4s, transform 0.3s',
                cursor: 'pointer',
                '&:hover': { boxShadow: '0 20px 50px rgba(0,0,0,0.5)' },
            }}
            onClick={onViewProfile}>
            <Box
                sx={{
                    height: 220,
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover img': { transform: 'scale(1.06)' },
                }}>
                {profile_picture ? (
                    <Box
                        component="img"
                        src={profile_picture}
                        alt={name ?? ''}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s ease' }}
                    />
                ) : (
                    <Typography sx={{ fontSize: 56, color: 'rgba(255,255,255,0.12)', fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        {(name ?? '?')[0].toUpperCase()}
                    </Typography>
                )}
                {activePlatforms.length > 0 && (
                    <Box sx={{ position: 'absolute', top: 14, right: 14, display: 'flex', gap: 0.75 }}>
                        {activePlatforms.map(p => (
                            <Box
                                key={p.key}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    backdropFilter: 'blur(12px)',
                                    bgcolor: 'rgba(0,0,0,0.55)',
                                    border: `1px solid ${p.color}44`,
                                }}>
                                {p.icon}
                            </Box>
                        ))}
                    </Box>
                )}
                {influencer_score !== undefined && influencer_score > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 14,
                            left: 14,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 999,
                            fontSize: 10,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            backdropFilter: 'blur(12px)',
                            bgcolor: 'rgba(0,0,0,0.5)',
                            border: '1px solid rgba(255,201,101,0.25)',
                            color: BRAND_LUXE_PRIMARY,
                        }}>
                        Score {influencer_score.toFixed(0)}
                    </Box>
                )}
                {/* Favourite + chat icons — bottom right of image */}
                <Box
                    sx={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 0.75 }}
                    onClick={e => e.stopPropagation()}>
                    <IconButton
                        onClick={onFavorite}
                        sx={{
                            width: 34,
                            height: 34,
                            backdropFilter: 'blur(12px)',
                            bgcolor: 'rgba(0,0,0,0.55)',
                            border: creator.is_favorited
                                ? '1px solid rgba(255,105,180,0.5)'
                                : '1px solid rgba(255,255,255,0.15)',
                            color: creator.is_favorited ? '#ff69b4' : '#fff',
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.15)', bgcolor: 'rgba(255,105,180,0.15)' },
                        }}>
                        {creator.is_favorited ? (
                            <FavoriteIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <FavoriteBorderIcon sx={{ fontSize: 16 }} />
                        )}
                    </IconButton>
                    <IconButton
                        onClick={onMessage}
                        sx={{
                            width: 34,
                            height: 34,
                            backdropFilter: 'blur(12px)',
                            bgcolor: 'rgba(0,0,0,0.55)',
                            border: `1px solid ${BRAND_LUXE_PRIMARY}66`,
                            color: BRAND_LUXE_PRIMARY,
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.15)', bgcolor: 'rgba(255,201,101,0.15)' },
                        }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </Box>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 19, mb: 0.25 }} noWrap>
                            {name ?? 'Unknown'}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: 11,
                                color: '#adaaaa',
                                textTransform: 'uppercase',
                                letterSpacing: '0.12em',
                            }}
                            noWrap>
                            {category}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        {simple ? (
                            <>
                                <Typography
                                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 19, color: BRAND_LUXE_PRIMARY }}>
                                    {creator.brand_match != null ? `${Math.round(creator.brand_match)}%` : '—'}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: '#adaaaa', textTransform: 'uppercase' }}>
                                    Brand fit
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Typography
                                    sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 19, color: BRAND_LUXE_PRIMARY }}>
                                    {formatFollowers(followers)}
                                </Typography>
                                <Typography sx={{ fontSize: 10, color: '#adaaaa', textTransform: 'uppercase' }}>
                                    Followers
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Box
                        sx={{
                            flex: 1,
                            bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                            p: 1.5,
                            borderRadius: 1,
                            textAlign: 'center',
                        }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: BRAND_LUXE_SECONDARY, fontSize: 15 }}>
                            {simple ? formatFollowers(followers) : formatEngagement(avg_engagement_rate)}
                        </Typography>
                        <Typography sx={{ fontSize: 9, textTransform: 'uppercase', color: '#adaaaa', mt: 0.5 }}>
                            {simple ? 'Followers' : 'Engagement'}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                            p: 1.5,
                            borderRadius: 1,
                            textAlign: 'center',
                        }}>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#ffffff', fontSize: 15 }} noWrap>
                            {country ?? '—'}
                        </Typography>
                        <Typography sx={{ fontSize: 9, textTransform: 'uppercase', color: '#adaaaa', mt: 0.5 }}>
                            Location
                        </Typography>
                    </Box>
                </Box>
                <Button
                    fullWidth
                    onClick={e => {
                        e.stopPropagation();
                        onInvite();
                    }}
                    sx={{
                        py: 1.75,
                        borderRadius: 999,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        fontSize: 13,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        color: '#392700',
                        background: `linear-gradient(to top right, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                        '&:hover': { boxShadow: '0 0 20px rgba(255,201,101,0.35)', filter: 'brightness(1.05)' },
                    }}>
                    Invite to campaign
                </Button>
            </Box>
        </Box>
    );
}
